import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { isPublicSiteVisitRespondUrl } from '../site-visit/public-site-visit-route';

@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  private timeoutDuration = environment.idleTimeout;
  private timeoutId: any;
  private readonly activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
  private activityHandler = () => this.resetTimer();

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private toastr: ToastrService
  ) {
    this.startListening();
  }

  private startListening() {
    this.resetTimer();
    // Register high-frequency activity listeners outside Angular so they don't
    // trigger change detection on every mousemove/keydown; the handler only
    // resets a timer (onTimeout re-enters the zone via ngZone.run when needed).
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach((event) =>
        window.addEventListener(event, this.activityHandler));
    });
  }

  private resetTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = setTimeout(() => this.onTimeout(), this.timeoutDuration);
    });
  }

  /**
   * 🔴 Never on the site contact's no-login site-visit page. That page has no session to expire, and
   * the timeout's two effects are both actively harmful there: the toast tells an external recipient
   * their session ended when they never had one, and the navigation replaces the URL — which on that
   * route is the only copy of their single-use link. An hour is well within how long someone reads a
   * page, puts the phone down and comes back, so this is a defect that would be hit rather than a
   * theoretical one.
   *
   * The timer keeps running (it is reset by activity like any other), so a session started later in
   * the same tab is still covered; only the expiry action is suppressed, and only on this route. The
   * broader change — suppressing it wherever there is no session hint, which would also cover the
   * auth screens — is deliberately NOT made here: those routes are already unaffected in practice
   * (the navigation is to the page they are on) and widening it is not this page's to decide.
   */
  private onTimeout() {
    if (isPublicSiteVisitRespondUrl(this.router.url)) {
      return;
    }
    this.ngZone.run(() => {
      this.toastr.error('Your session has expired, please login again.');
      this.router.navigate(['/login']);
    });
  }

  public stopListening() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.activityEvents.forEach((event) =>
      window.removeEventListener(event, this.activityHandler));
  }
}
